import IDeal, {
  DealStatus,
  IDealsWithSubscriptionPayload,
  IPrimaryIssuer,
} from '@/creditflowresearch/domain/vo/IDeal';
import { ICorporateDeal } from '@/creditflowresearch/domain/vo/Deals/CorporateDeal';
import { Product } from '@/creditflowresearch/domain/Product';
import { AbsProduct } from '@/creditflowresearch/domain/Product';
import { filterSupportedProducts } from '@/creditflowresearch/application/Dictionaries/helpers';
import type { IDomainDictionaries } from '@/creditflowresearch/domain';
import { IDictionaryIndustryMapping } from '@/creditflowresearch/domain/vo/IDictionary';
import { IUserPlanResponse } from '@creditflowresearch/user-plan/vo/IUserPlanResponse';
import UserPlan, { UserPlanType } from '@creditflowresearch/user-plan/UserPlan';
import { selectProductAndRegionIdsFromQuery } from '@/creditflowresearch/application/PageFilters/selectProductAndRegionIdsFromQuery';
import { ISubscriptionStatus } from '@/creditflowresearch/domain/vo/ISubscriptionStatus';

export const dealStatusName = {
  [DealStatus.Pulled]: 'Pulled',
  [DealStatus.InMarket]: 'In Market',
  [DealStatus.Priced]: 'Priced',
};

export const initialDealsState = {
  collection: [],
  isCollectionFetching: true,
  isFetchingMoreDeals: false,
  bufferCollection: [],
  totalCount: 0,
  page: 1,
  perPage: 10,
  pageTitle: '',
  subscriptions: {
    isFetching: false,
    isSubscribed: false,
    isSubscribeModalVisible: false,
    subscriptionId: '',
  },
};

export interface IDealByIdState {
  deal: IDeal | null,
  hasServerError?: boolean,
}

export const initialDealByIdState: IDealByIdState = {
  deal: null,
  hasServerError: false,
};

export interface ICompanyByIdState {
  viewId: number | null,
  id: string | null,
  name?: string,
  industryId?: string,
  subindustryId?: string,
  publicExchange?: any,
  publicTicker?: string,
  abbreviation?: string,
  issuerTicker?: string,
  hasServerError: boolean,
}

export const initialCompanyByIdState: ICompanyByIdState = {
  viewId: null,
  id: null,
  name: null,
  hasServerError: false,
};

export interface IDealsSubscriptionResponse {
  totalCount: number,
  collection: IDeal[],
  hasServerError?: boolean,
  subscriptionStatus: ISubscriptionStatus,
  pageFilters?: IDealsWithSubscriptionPayload,
}

export const getInitialDealsSubscriptionState = (userPlan: UserPlanType): IDealsSubscriptionResponse => ({
  totalCount: 0,
  collection: [],
  hasServerError: false,
  subscriptionStatus: {
    subscriptionId: '',
    subscriptionSubgroupName: '',
    subscriptionName: '',
    isSubscribed: false,
  },
  pageFilters: {
    page: 1,
    perPage: 10,
    filters: {
      hasEitherPricingOrAnnouncedDate: false,
      includedDealViewIds: [],
      includedStatusGroups: getDealStatusAccessed(userPlan),
      includedProductIds: [],
      includedRegionIds: [],
    },
    filterType: '',
  },
});

export const formatDealName = (deal: Partial<IDeal | ICorporateDeal> = {}): string => {
  if (!Object.keys(deal).length) {
    return '';
  }

  const issuer: Partial<IPrimaryIssuer> = deal.primaryIssuer || {};
  const isProductAbs = deal.product ? Product.isOfType(deal.product.id, AbsProduct.TYPE) : true;
  const parts = [(issuer.name || '')];

  if (issuer.issuerTicker) {
    parts.push(`(${ issuer.issuerTicker })`);
  }

  if (isProductAbs) {
    parts.push(deal.seriesName || '');
  }

  return parts.join(' ');
};

export interface IDealStatusFilterItem {
  abbreviation: string,
  id: DealStatus,
  isActive: boolean,
  name: string,
  hasAccess: boolean,
  isDisabled: boolean,
  title: string,
}

// TODO: remove when the European market opening period ends (US-5562)
export const getDealStatusFilterItems = (
  selectedDealStatuses: DealStatus[],
  userPlan: UserPlanType,
  hasOpenStatusGroups: boolean = false,
): IDealStatusFilterItem[] => (
  Object.values(DealStatus).map((status) => ({
    abbreviation: dealStatusName[status],
    id: status,
    isActive: selectedDealStatuses.includes(status),
    name: dealStatusName[status],
    hasAccess: hasOpenStatusGroups || UserPlan.isPro(userPlan) || status === DealStatus.Priced,
    isDisabled: !hasOpenStatusGroups && !UserPlan.isPro(userPlan) && status !== DealStatus.Priced,
    title: dealStatusName[status],
  }))
);

// TODO: remove when the European market opening period ends (US-5562)
export const getDealStatusAccessed = (
  userPlan: UserPlanType,
  hasOpenStatusGroups: boolean = false,
): DealStatus[] => {
  if (hasOpenStatusGroups || UserPlan.isPro(userPlan)) {
    return Object.values(DealStatus);
  }

  return [DealStatus.Priced];
};

export const getFiltersDealStatus = (dealStatuses: DealStatus[], newStatus: DealStatus) => (
  dealStatuses.includes(newStatus)
    ? dealStatuses.filter((status) => status !== newStatus)
    : [...dealStatuses, newStatus]
);

export const getProducts = ({ domainDictionaries, userPlanState, productsRegions }: {
  domainDictionaries: IDomainDictionaries,
  userPlanState: IUserPlanResponse,
  productsRegions: ReturnType<typeof selectProductAndRegionIdsFromQuery>,
}) => {
  const productsList = filterSupportedProducts(domainDictionaries.products ?? []);
  const { products } = userPlanState;
  const { productIds } = productsRegions;
  const availableProductIds: string[] = productsList.reduce((acc, product) => (
    products.includes(product.id) && productIds.includes(product.id)
        ? [...acc, product.id]
        : acc
    ), [],
  );
  const activeProductId = availableProductIds.length > 0 ? availableProductIds[0] : null;

  return {
    availableProductIds,
    activeProductId,
  };
};

export const getIndustriesPayload = (industry: IDictionaryIndustryMapping) => {
  let filters = {
    includedIndustryIds: [],
    includedSubindustryIds: [],
  };

  if (industry && industry.industryId) {
    filters.includedIndustryIds = [industry.industryId];
    if (industry.subindustryId) {
      filters.includedSubindustryIds = [industry.subindustryId];
    }
  }

  return filters;
};
